import React, { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRolePermissionsContext } from "../context/RolePermissionsContext";
import {
    useCreateCustomRoleMutation,
    useUpdateCustomRoleMutation,
} from "@me-team/host/main/CustomRoles/graphql/CustomRoles.hooks";
import { useDeleteOrRestoreCustomRoleMutation } from "@me-team/host/main/RoleManagement/graphql/roleManagement.hooks";
import { RoleDetailsPageService } from "../service/RoleDetailsPageService";
import { useNavigate, useParams } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { requestRoleAccessReload } from "@me-team/host/main/globalVar/requestRoleAccessReload";
import { RoleType } from "../RoleDetailsPage";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { cloneNameToApi } from "@me-team/common/src/components/Me-menu/utils/cloneNameUtils";

type RoleDetailsButtonsProps = {
    onSubmitRole: (roleType: RoleType, error: string | null) => void;
    roleNameError: string | null;
    roleId: number | null;
};

const RoleDetailsButtons: React.FC<RoleDetailsButtonsProps> = ({ onSubmitRole, roleNameError, roleId }) => {
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { branchId } = useParams();
    const { t: translate } = useTranslation();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { permissions, mode } = useRolePermissionsContext();
    const canCreateRole = useRoleAccess(CompanyAccess.RoleCreateClone);
    const canEditRole = useRoleAccess(CompanyAccess.RoleEdit);
    const canCloneRole = useRoleAccess(CompanyAccess.RoleCreateClone);
    const canDeleteRole = useRoleAccess(CompanyAccess.RoleDelete);

    const submitBtnText = (() => {
        switch (mode) {
            case "create":
                return "Create";
            case "restore":
                return "Restore";
            default:
                return "Save";
        }
    })();
    const isViewMode = mode === "view";
    const canSubmitRole = (() => {
        switch (mode) {
            case "create":
                return canCreateRole;
            case "edit":
                return canEditRole;
            case "clone":
                return canCloneRole;
            case "restore":
                return canDeleteRole;
            default:
                return false;
        }
    })();
    const cancelBtnText = isViewMode ? "Back" : "Cancel";
    const hasRoleNameError = Boolean(roleNameError);
    const isSubmitDisabled = isSubmitting || !canSubmitRole || hasRoleNameError;

    const [createCustomRole] = useCreateCustomRoleMutation({
        fetchPolicy: "no-cache",
        context: {
            errorType: "local",
        },
        onCompleted: () => {
            navigate(`/branch/role-management/${branchId}`, { state: { text: "Created", toast: true } });
        },
        onError: (error) => {
            if (error.graphQLErrors?.length) {
                const category = error.graphQLErrors[0].extensions?.category;

                if (category === "role.already_exists") {
                    onSubmitRole("name", "Role with this name already exists");
                }
                if (category === "arguments_validation_error") {
                    onSubmitRole("permissions", "Add at least one permission to create a role.");
                }
            }
        },
    });

    const [updateCustomRole] = useUpdateCustomRoleMutation({
        fetchPolicy: "no-cache",
        context: {
            errorType: "local",
        },
        onCompleted: () => {
            requestRoleAccessReload();
            if (mode !== "restore") {
                navigate(`/branch/role-management/${branchId}`, { state: { text: "Saved", toast: true } });
            }
        },
        onError: (error) => {
            if (error.graphQLErrors?.length) {
                const category = error.graphQLErrors[0].extensions?.category;

                if (category === "role.already_exists") {
                    onSubmitRole("name", "Role with this name already exists");
                }
                if (category === "arguments_validation_error") {
                    onSubmitRole("permissions", "Add at least one permission to create a role.");
                }
            }
        },
    });

    const [deleteOrRestoreCustomRole] = useDeleteOrRestoreCustomRoleMutation({
        fetchPolicy: "no-cache",
        context: {
            errorType: "local",
        },
        onCompleted: () => {
            navigate(`/branch/role-management/${branchId}`, { state: { text: "Restored", toast: true } });
        },
    });

    const onCreateRole = async () => {
        if (!canSubmitRole) return;

        if (!permissions || roleNameError) {
            return;
        }

        const name = permissions.role_info.name?.trim();

        if (!name) {
            onSubmitRole("name", "Role name is required");
            return;
        }

        setIsSubmitting(true);

        const input = RoleDetailsPageService.buildCreateCustomRoleInput(permissions);
        input.name = cloneNameToApi(input.name, translate);

        if (mode === "create" || mode === "clone") {
            await createCustomRole({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    input,
                },
            });
        }

        if (mode === "edit") {
            await updateCustomRole({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    roleId: roleId,
                    input,
                },
            });
        }

        if (mode === "restore") {
            const updateResult = await updateCustomRole({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    roleId: roleId,
                    input,
                },
            });

            if (!updateResult?.data?.updateCustomRole) {
                setIsSubmitting(false);
                return;
            }

            await deleteOrRestoreCustomRole({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    roleId: roleId,
                    deleted: false,
                },
            });
            return;
        }
        setIsSubmitting(false);
    };

    return (
        <Row className={"mt-5"}>
            <Col sm={12} md={4} xl={3} className={"mb-3 mb-md-0"}>
                <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={() => {
                        navigate(`/branch/role-management/${branchId}`);
                    }}
                >
                    {translate(cancelBtnText)}
                </Button>
            </Col>
            {!isViewMode && canSubmitRole && (
                <Col sm={12} md={4} xl={3}>
                    <Button variant="primary" className="w-100" onClick={onCreateRole} disabled={isSubmitDisabled}>
                        {translate(submitBtnText)}
                    </Button>
                </Col>
            )}
        </Row>
    );
};

export default RoleDetailsButtons;
